import { zodResolver } from "@hookform/resolvers/zod";
import { SubmitHandler, useForm } from "react-hook-form";
import StyledSpinner from "@/components/styled/StyledSpinner";
import { ControlledUnifiedInput } from "@/components/ui/customs/controlled-unified-input";
import { Modal } from "@/components/ui/customs/modal";
import { Button } from "@/components/ui/button";
import { useUpdateCompanyParametersBO } from "@/api/company.api";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import {
  ParametersBusinessNameFormData,
  ParametersBusinessNameSchema,
} from "@/types/forms/bo/clientsParameters.form";

type UpdateBusinessNameModalProps = {
  closeModal: () => void;
  businessName: string;
  title: string;
};

export const UpdateBusinessNameModal = ({
  closeModal,
  businessName,
  title,
}: UpdateBusinessNameModalProps) => {
  const { t } = useTranslation("boClients");
  const [companyId] = useLocation().pathname.split("/").slice(-1);

  const { mutateAsync: updateCompanyParametersBO, isPending } = useUpdateCompanyParametersBO();

  const businessNameForm = useForm<ParametersBusinessNameFormData>({
    resolver: zodResolver(ParametersBusinessNameSchema),
    mode: "onSubmit",
    reValidateMode: "onChange",
    defaultValues: {
      businessName,
    },
  });

  const onSubmit: SubmitHandler<ParametersBusinessNameFormData> = async (data) => {
    const ret = await updateCompanyParametersBO({
      id: companyId,
      payload: data,
    });
    if (ret?.status === 200) {
      closeModal();
    }
  };

  return (
    <Modal
      open
      onClose={closeModal}
      title={title}
      size="sm"
      contentClassName="max-w-[480px]"
      footer={
        <Button type="submit" form="businessNameForm" disabled={isPending} className="w-full">
          {isPending ? <StyledSpinner /> : t("businessNameModal.submit")}
        </Button>
      }
    >
      <form
        id="businessNameForm"
        onSubmit={businessNameForm.handleSubmit(onSubmit)}
        className="flex flex-col gap-4 pt-2"
      >
        <ControlledUnifiedInput
          control={businessNameForm.control}
          name="businessName"
          label={t("businessNameModal.businessNameLabel")}
          defaultValue={businessName}
          inputType="text"
        />
      </form>
    </Modal>
  );
};
